import { uniq } from 'lodash-es';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { isRoleOfType, RoleType } from '@iss/libs/permissions';
import { Customer, Role } from '@iss/libs/graphql';

import { AuthDetails } from './auth.types';
import { AuthState } from './auth.state';
import { authActions } from './auth.actions';

import { AsyncStatus, pickAsyncData } from '@iss/wo-common/types';

const initialState: AuthState = {
  account: { status: AsyncStatus.INITIAL },
  details: { status: AsyncStatus.INITIAL },
};

export const authFeature = createFeature({
  name: 'auth',
  reducer: createReducer(
    initialState,
    on(authActions.authenticated, (state, { account }): AuthState => ({
      ...state,
      account: { status: AsyncStatus.SUCCESS, data: account },
    })),
    on(authActions.logout, (state): AuthState => ({
      ...state,
      account: initialState.account,
    })),
    on(authActions.detailsLoadingFailure, (state): AuthState => ({
      ...state,
      details: { status: AsyncStatus.FAILURE },
    })),
    on(authActions.detailsLoading, (state): AuthState => ({
      ...state,
      details: { status: AsyncStatus.LOADING },
    })),
    on(authActions.detailsLoaded, (state, { details }): AuthState => ({
      ...state,
      details: {
        status: AsyncStatus.SUCCESS,
        data: details,
      },
    })),
  ),
  extraSelectors: self => {
    const selectAsyncAccount = createSelector(
      self.selectAccount,
      account => pickAsyncData(account, null),
    );

    const selectAsyncDetails = createSelector(
      self.selectDetails,
      details => pickAsyncData(details, null),
    );

    const selectIsAuthenticated = createSelector(
      self.selectAccount,
      self.selectDetails,
      (account, details) => {
        if ([ AsyncStatus.INITIAL, AsyncStatus.LOADING ].includes(account.status)) return undefined;
        if ([ AsyncStatus.INITIAL, AsyncStatus.LOADING ].includes(details.status)) return undefined;
        return [ account.status, details.status ].every(status => status === AsyncStatus.SUCCESS);
      },
    );

    const selectTokenUsername = createSelector(
      selectAsyncAccount,
      account => account?.username || '',
    );

    const selectDetailsUsername = createSelector(
      selectAsyncDetails,
      details => details?.displayName || '',
    );

    const selectUsername = createSelector(
      selectDetailsUsername,
      selectTokenUsername,
      (details, token) => details || token || 'Guest',
    );

    const selectUserEmail = createSelector(
      selectAsyncAccount,
      account => account?.email || '',
    );

    const findEmployers = (roles: readonly Role[], details: AuthDetails | null, type: string) => {
      const positions = details?.flatPositions
        .filter(({ role }) => roles.includes(role))
        .filter((position) => position.type === type) || [];
      return uniq<Customer['id']>(positions.map(({ employer }) => employer));
    };

    const selectDistinctRoles = createSelector(
      selectAsyncDetails,
      details => details?.distinctRoles || [],
    );

    const selectDistinctCountries = createSelector(
      selectAsyncDetails,
      details => details?.distinctCountries || [],
    );

    const selectHasMultipleCountries = createSelector(
      selectDistinctCountries,
      distinctCountries => distinctCountries.length > 1,
    );

    const selectHasOnlyOneRole = createSelector(
      selectDistinctRoles,
      distinctRoles => distinctRoles.length === 1,
    );

    const selectHasMultipleRoles = createSelector(
      selectDistinctRoles,
      distinctRoles => distinctRoles.length > 1,
    );

    const selectRoles = createSelector(
      selectDistinctRoles,
      roles => new Set(roles),
    );

    const selectPermissions = createSelector(
      selectAsyncDetails,
      details => new Set(details?.distinctPermissions || []),
    );

    const selectIsAdmin = createSelector(
      selectDistinctRoles,
      roles => roles.some(role => isRoleOfType(role, RoleType.Admin)),
    );

    const selectIsCentralTeam = createSelector(
      selectDistinctRoles,
      roles => roles.some(role => isRoleOfType(role, RoleType.CentralTeam)),
    );

    const selectIsSupplier = createSelector(
      selectDistinctRoles,
      roles => roles.some(role => isRoleOfType(role, RoleType.Supplier)),
    );

    const selectAvailableSuppliers = createSelector(
      selectAsyncDetails,
      details => findEmployers([
        Role.Supplier,
      ], details, 'SUPPLIER'),
    );

    const selectHasMultipleSuppliers = createSelector(
      selectAvailableSuppliers,
      suppliers => suppliers.length > 1,
    );

    const selectOnlyOneSupplier = createSelector(
      selectAvailableSuppliers,
      suppliers => suppliers.length === 1,
    );

    const selectHasToken = createSelector(selectAsyncAccount, Boolean);

    return {
      selectHasToken,
      selectIsAuthenticated,
      selectUsername,
      selectUserEmail,
      selectAvailableSuppliers,
      selectOnlyOneSupplier,
      selectHasMultipleSuppliers,
      selectIsAdmin,
      selectIsCentralTeam,
      selectIsSupplier,
      selectRoles,
      selectDistinctRoles,
      selectDistinctCountries,
      selectHasMultipleCountries,
      selectHasOnlyOneRole,
      selectHasMultipleRoles,
      selectPermissions,
    };
  },
});
